import { GraphQLClient } from "graphql-request";
import { shopifyApiConfig } from "./shopifyApiConfig";

// Import GraphQL queries from the functions directory
// Note: These queries are defined in gatsby/functions/requestConfig.ts
// We'll need to create a separate queries file for the client-side usage

// Cart-related GraphQL queries for Storefront API 2024-01
export const CART_QUERY = `
  query getCart($id: ID!) {
    cart(id: $id) {
      id
      createdAt
      updatedAt
      checkoutUrl
      discountCodes {
        applicable
        code
      }
      note
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                image {
                  originalSrc
                  transformedSrc(maxHeight: 150, maxWidth: 115)
                }
                currentlyNotInStock
                availableForSale
                quantityAvailable
                product {
                  id
                  handle
                  title
                }
                sku
                title
                compareAtPriceV2 {
                  amount
                  currencyCode
                }
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
      attributes {
        key
        value
      }
      estimatedCost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        totalTaxAmount {
          amount
          currencyCode
        }
        totalDutyAmount {
          amount
          currencyCode
        }
      }
      buyerIdentity {
        email
        phone
        countryCode
      }
    }
  }
`;

export const CART_CREATE = `
  mutation createCart($cartInput: CartInput) {
    cartCreate(input: $cartInput) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        discountCodes {
          applicable
          code
        }
        note
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  image {
                    originalSrc
                    transformedSrc(maxHeight: 150, maxWidth: 115)
                  }
                  currentlyNotInStock
                  availableForSale
                  quantityAvailable
                  product {
                    id
                    handle
                    title
                  }
                  sku
                  title
                  compareAtPriceV2 {
                    amount
                    currencyCode
                  }
                  priceV2 {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
        estimatedCost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          countryCode
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

export const CART_ADD = `
  mutation cartLinesAdd($lines: [CartLineInput!]!, $cartId: ID!) {
    cartLinesAdd(lines: $lines, cartId: $cartId) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        discountCodes {
          applicable
          code
        }
        note
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  image {
                    originalSrc
                    transformedSrc(maxHeight: 150, maxWidth: 115)
                  }
                  currentlyNotInStock
                  availableForSale
                  quantityAvailable
                  product {
                    id
                    handle
                    title
                  }
                  sku
                  title
                  compareAtPriceV2 {
                    amount
                    currencyCode
                  }
                  priceV2 {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
        estimatedCost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          countryCode
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

export const CART_LINES_UPDATE = `
  mutation cartLinesUpdate($lines: [CartLineUpdateInput!]!, $cartId: ID!) {
    cartLinesUpdate(lines: $lines, cartId: $cartId) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        discountCodes {
          applicable
          code
        }
        note
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  image {
                    originalSrc
                    transformedSrc(maxHeight: 150, maxWidth: 115)
                  }
                  currentlyNotInStock
                  availableForSale
                  quantityAvailable
                  product {
                    id
                    handle
                    title
                  }
                  sku
                  title
                  compareAtPriceV2 {
                    amount
                    currencyCode
                  }
                  priceV2 {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
        estimatedCost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          countryCode
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

export const CART_LINES_REMOVE = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        discountCodes {
          applicable
          code
        }
        note
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  image {
                    originalSrc
                    transformedSrc(maxHeight: 150, maxWidth: 115)
                  }
                  currentlyNotInStock
                  availableForSale
                  quantityAvailable
                  product {
                    id
                    handle
                    title
                  }
                  sku
                  title
                  compareAtPriceV2 {
                    amount
                    currencyCode
                  }
                  priceV2 {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
        estimatedCost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
        buyerIdentity {
          email
          phone
          countryCode
        }
      }
      userErrors {
        code
        field
        message
      }
    }
  }
`;

// Product-related GraphQL queries
export const PRODUCT_QUERY = `
  query getProduct($id: ID!) {
    node(id: $id) {
      ... on Product {
        id
        handle
        title
        totalVariants
        images(first: 100) {
          edges {
            node {
              id
              originalSrc
            }
          }
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              price
              displayName
            }
          }
        }
        metafield(namespace: "sync", key: "productData") {
          value
          id
        }
      }
    }
  }
`;

// Products collection query
export const PRODUCTS_COLLECTION_QUERY = `
  query getProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      edges {
        node {
          id
          handle
          title
          images(first: 100) {
            edges {
              node {
                id
                originalSrc
              }
            }
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                sku
                availableForSale
                quantityAvailable
                compareAtPriceV2 {
                  amount
                  currencyCode
                }
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

// GraphQL client instance for Storefront API 2024-01
export const shopifyGraphQLClient = shopifyApiConfig.client;

// Helper function to make GraphQL requests with proper error handling
export const makeGraphQLRequest = async <T = any>(query: string, variables?: Record<string, any>): Promise<T> => {
  try {
    const response = await shopifyGraphQLClient.request<T>(query, variables);
    return response;
  } catch (error) {
    console.error("GraphQL request failed:", error);
    throw new Error(`GraphQL request failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
};

// Export all queries for use in other files
export const shopifyQueries = {
  CART_QUERY,
  CART_CREATE,
  CART_ADD,
  CART_LINES_UPDATE,
  CART_LINES_REMOVE,
  PRODUCT_QUERY,
  PRODUCTS_COLLECTION_QUERY,
};

export default shopifyGraphQLClient;
